import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useScheduleActivityAccess, useScheduleRequestFeaturesAccess } from '@frontend/schedule-helpers';
import { Link } from '@frontend/weave-router';
import { theme } from '@frontend/theme';
import { Accordion, TextLink } from '@frontend/design-system';
import { useOpenedRequest } from '../../hooks/use-opened-request';
import { useSchedulePulseRequestList } from '../../hooks/use-schedule-pulse-request-list';
import { useScheduleRequestNotificationsAsRead } from '../../hooks/use-schedule-request-notifications-as-read';
import { ScheduleRequestTray } from '../../ScheduleRequestModal/ScheduleRequestWrappers/ScheduleRequestTray';
import { ScheduleRequestsTrackingIds } from '../../trackingIds';
import { ScheduleRequestListContent } from './../ScheduleRequestListContent';
import { SchedulePulseRequestListItem } from './SchedulePulseRequestListItem';

interface SchedulePulseRequestListProps {
  locationId: string;
  height?: number | string;
}

export const SchedulePulseRequestList = ({ locationId, height }: SchedulePulseRequestListProps) => {
  const { t } = useTranslation('scheduleCalendarActions');
  const { markNotificationsAsRead } = useScheduleRequestNotificationsAsRead();
  const { refetchScheduleRequestList, isEmptyScheduleRequestList, infiniteListQuery } = useSchedulePulseRequestList({
    locationId,
  });

  const { selectedRequest, setSelectedRequest } = useOpenedRequest({ locationId, infiniteListQuery });
  const { hasScheduleRequestFeaturesAccess } = useScheduleRequestFeaturesAccess();
  const { isScheduleActivityEnabled, isLoading: isLoadingScheduleActivityAccess } = useScheduleActivityAccess();

  return (
    <>
      <Accordion
        css={{ ...(isEmptyScheduleRequestList && { background: theme.colors.white }), flexGrow: 1 }}
        variant='blank'
        chevronSize={16}
        startingValue=''
        size='large'
        controlledValue={selectedRequest}
        onChange={(val) => {
          if (!!val) {
            markNotificationsAsRead(val);
          }
          setSelectedRequest(val);
        }}
      >
        <ScheduleRequestListContent
          refetchScheduleRequestList={refetchScheduleRequestList}
          infiniteListQuery={infiniteListQuery}
          height={height}
          modalWrapper={ScheduleRequestTray}
          scheduleRequestListItem={SchedulePulseRequestListItem}
        />
      </Accordion>
      {hasScheduleRequestFeaturesAccess && !isLoadingScheduleActivityAccess && (
        <div css={viewHistoryContainerStyles}>
          <TextLink
            as={Link}
            css={{ display: 'flex', alignItems: 'center' }}
            size='medium'
            weight='bold'
            to={
              isScheduleActivityEnabled
                ? `/schedule/schedule-activity?locationId=${locationId}`
                : `/schedule/request-history?locationId=${locationId}`
            }
            trackingId={ScheduleRequestsTrackingIds.viewHistoryBtn}
          >
            <Icon name='external-link-small' css={{ marginRight: theme.spacing(0.5) }} />
            {t('View History')}
          </TextLink>
        </div>
      )}
    </>
  );
};

const viewHistoryContainerStyles = css({
  display: 'flex',
  justifyContent: 'center',
  padding: theme.spacing(2),
  background: theme.colors.white,
  borderTop: `1px solid ${theme.colors.neutral10}`,
});
